<template>
  <div class="rounded-xl border border-base-300 bg-base-100 p-4 space-y-4">
    <!-- Header and actions -->
    <div class="flex justify-between items-center">
       <div class="flex flex-col">
        <h2 class="text-lg font-semibold">{{ chartTitle }}</h2>
        <p class="text-xs text-light">{{ chartDescription }}</p>
      </div>
      <div class="flex gap-2">
        <AButton class="btn-soft btn-accent btn-xs">Current year</AButton>
        <AButton class="btn-soft btn-accent btn-xs">Historical</AButton>
      </div>
    </div>

    <!-- Totals row -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <!-- Current & Previous -->
      <div class="flex gap-8 text-sm">
        <div class="flex items-center gap-2">
          <div class="w-2.5 h-2.5 rounded-full bg-accent"></div>
          <div>
            <p class="text-xs text-base-content/60">Current year</p>
            <p class="text-lg font-semibold text-base-content">
              {{
                currentYearData.length
                    ? formatThousands(currentYearData.reduce((a, b) => a + b, 0), symbol)
                    : '0'
              }}
            </p>
          </div>
        </div>
        <div v-if="hasPreviousData" class="flex items-center gap-2">
          <div class="w-2.5 h-2.5 rounded-full bg-primary"></div>
          <div>
            <p class="text-xs text-base-content/60">Previous year</p>
            <p class="text-lg font-semibold text-base-content">
              {{ formatThousands(previousYearData.reduce((a, b) => a + b, 0), symbol) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Optional: Show Fee -->
      <div v-if="feeDisplay" class="text-xs text-base-content/60">
        <span class="font-bold">CharterPlace</span> Fee
        <span
            class="ml-1 badge badge-sm font-semibold text-success bg-success/10 border border-success/20 rounded-full"
        >
          {{ feeDisplay }}
        </span>
      </div>

      <!-- Flights Box -->
      <DataBox
          label="Number of flights operated"
          :value="numberOfFlights"
          :iconComponent="CardIcon"
          :iconComponentProps="{ icon: 'bi-airplane' }"
          iconComponentClass="bg-accent/10 text-accent"
          :iconComponentSm="true"
          :show-right-border="false"
          roundedIcon="rounded-lg"
      />
    </div>

    <!-- Chart Canvas -->
    <div class="mt-4">
      <div v-if="hasData" class="relative h-80">
        <canvas ref="canvasEl"></canvas>
      </div>
      <DashboardChartNoData v-else />
    </div>

  </div>
</template>

<script setup>
import { computed, nextTick, onActivated, onBeforeUnmount, onDeactivated, ref, shallowRef, watch } from 'vue'
import {BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip} from 'chart.js'

import AButton from "@/components/atoms/AButton.vue"
import {MONTHS} from "@/common/constants.js"
import {formatThousands} from "@/common/functions.js";
import CardIcon from "@/components/atoms/CardIcon.vue"
import DataBox from "@/components/atoms/DataBox.vue"
import DashboardChartNoData from "@/components/atoms/DashboardChartNoData.vue";

function currencyToSymbol(code) {
  if (!code || code.length <= 1) return code || '$'
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency: code.toUpperCase(), currencyDisplay: 'narrowSymbol' })
      .formatToParts(0)
      .find(p => p.type === 'currency')?.value || code
  } catch { return code }
}

// Register once per module
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title)

const props = defineProps({
  chartTitle: String,
  chartDescription: String,
  currency: {
    type: String,
    default: '$'
  },
  flightsLabel: String,
  numberOfFlights: {
    type: Number,
    default: 0
  },
  currentYearData: {
    type: Array,
    default: () => [],
  },
  previousYearData: {
    type: Array,
    default: () => [],
  },
  fee: {
    type: [String, Number],
    default: null,
  },
})

const symbol = computed(() => currencyToSymbol(props.currency))

const feeDisplay = computed(() => {
  if (props.fee === null || props.fee === undefined) return null
  return typeof props.fee === 'number' ? `${(props.fee * 100).toFixed(0)}%` : props.fee
})

const canvasEl = ref(null)
const chartRef = shallowRef(null)

const hasPreviousData = computed(() =>
  Array.isArray(props.previousYearData) && props.previousYearData.some(v => v !== 0)
)

const hasData = computed(() =>
  (Array.isArray(props.currentYearData) && props.currentYearData.some(v => v !== 0)) || hasPreviousData.value
)

function destroyChart() {
  if (chartRef.value) {
    chartRef.value.destroy()
    chartRef.value = null
  }
}

function renderChart() {
  if (!canvasEl.value || !hasData.value) {
    destroyChart()
    return
  }

  // Always destroy before creating (prevents "Canvas is already in use")
  destroyChart()

  const ctx = canvasEl.value.getContext('2d')

  try {
    chartRef.value = new ChartJS(ctx, {
      type: 'bar',
      data: {
        labels: MONTHS,
        datasets: [
          {
            label: 'Current Year',
            data: props.currentYearData,
            backgroundColor: '#3c89f9',
            borderRadius: 4,
          },
          {
            label: 'Previous Year',
            data: props.previousYearData,
            backgroundColor: '#0c1f35',
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            mode: 'index',
            intersect: false,
          },
          title: {
            display: false,
          },
        },
        animation: {
          duration: 1200,
          easing: 'easeOutQuart',
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
        scales: {
          y: {
            beginAtZero: true,
            suggestedMax: Math.max(...props.previousYearData, ...props.currentYearData) * 1.15,
            ticks: {
              count: 6,
              display: true,
              color: '#64748b',
              callback: (value) => {
                const s = symbol.value
                if (value === 0) return '0'
                if (value >= 1_000_000) return `${s}${(value / 1_000_000).toFixed(1)}M`
                if (value >= 1_000)    return `${s}${(value / 1_000).toFixed(1)}K`
                return `${s}${value}`
              },
            },
            grid: {
              drawBorder: false,
              drawTicks: false,
              color: 'rgba(0,0,0,0.05)',
            },
          },
          x: {
            grid: {
              display: false,
              drawBorder: false,
            },
            ticks: {
              color: '#64748b',
            },
          },
        },
      },
    })
  } catch (error) {
    console.error('🔥 Error rendering chart:', error)
  }
}

const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve))

// Re-render when props change
watch(
    () => [props.currentYearData, props.previousYearData],
    async () => {
      await nextTick()
      await nextFrame()
      renderChart()
    },
    {immediate: true, deep: false}
)

// Clean up on navigation/unmount
onBeforeUnmount(destroyChart)

// If using <KeepAlive>, also handle activation/deactivation
onDeactivated(destroyChart)
onActivated(async () => {
  await nextTick()
  await nextFrame()
  renderChart()
})
</script>